import { Box, List, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
// import { Note, SelectedFile, CommentListProps, ReadByEntry, UpdateItem, Attachments } from './types'
import { useQueryClient } from '@tanstack/react-query'
import { useBatchReadComments } from 'api/aws/batch/useBatchReadComments'
import AttachmentViewer from './AttachmentViewer'
import CommentCard from './CommentCard'

import { Attachments, BatchRequest, CommentData, ReadByEntry, ReadByUpdateItem, SelectedFile, User } from 'types'

interface CommentListProps {
  notes: CommentData[]
  isDeleting?: boolean
  users: User[]
  openFrame: (embedUrl: string) => void
  onDeleteNote: (note: CommentData) => void
  onDeleteAttachment: (commentId: string, attachmentIndex: number) => void
  expanded: boolean
  context: string
  isCommentOpen?: boolean
  setIsCommentOpen?: React.Dispatch<React.SetStateAction<boolean>>
}
type DivMap = Record<string, HTMLDivElement | null>

const CommentList: React.FC<CommentListProps> = ({
  notes,
  isDeleting,
  users,
  openFrame,
  onDeleteNote,
  onDeleteAttachment,
  expanded,
  context,
  isCommentOpen,
  setIsCommentOpen,
}) => {
  const [localComments, setLocalComments] = useState<CommentData[]>(notes ?? [])
  // const { users: testUsers } = useUsers(true)
  // const currentUser =
  //   testUsers?.filter(u => u?.name === 'Patric Cavanaugh')?.[0] || JSON.parse(localStorage.getItem('currentUser'))
  const currentUser = JSON.parse(localStorage.getItem('currentUser'))
  const [viewerOpen, setViewerOpen] = useState(false)
  const [currentAttachments, setCurrentAttachments] = useState<Attachments[]>([])
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState<string>('') // for controlled editor
  const [editingFiles, setEditingFiles] = useState<SelectedFile[]>([])
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const scrollRef = useRef<HTMLUListElement>(null)

  const commentRefs = useRef<Record<string, HTMLLIElement | null>>({})
  const confirmRefs = useRef<DivMap>({} as DivMap)
  const { mutateAsync: batchReadComments } = useBatchReadComments()
  const pendingBatch = useRef<ReadByUpdateItem[]>([])
  const queryClient = useQueryClient()

  const flushBatch = useCallback(async () => {
    const batchToSend = pendingBatch.current.splice(0) // ReadByUpdateItem[]
    if (batchToSend.length === 0) return
    try {
      const payload: BatchRequest = { updates: batchToSend }
      await batchReadComments(payload)
    } catch (err) {
      console.error('Batch update failed, re‐enqueueing:', err)
      pendingBatch.current.push(...batchToSend)
    }
  }, [batchReadComments])

  // Debounce “flushBatch” whenever localComments changes
  useEffect(() => {
    const timer = setTimeout(() => {
      flushBatch()
    }, 500)
    return () => clearTimeout(timer)
  }, [flushBatch, localComments])

  const handleMarkAsRead = useCallback((commentUuid: string) => {
    // Early return if currentUser is not available
    if (!currentUser?.user_uuid) {
      console.warn('[CommentList] Cannot mark as read - no currentUser found')
      return
    }

    const now = new Date().toISOString()

    setLocalComments(prev =>
      prev.map(note => {
        if (note.comment_uuid !== commentUuid) {
          return note
        }

        // 1) Grab the raw readBy array (could contain mixed shapes)
        const existingRaw = note.readBy && Array.isArray(note.readBy) ? note.readBy : []

        // 2) Filter out only those entries that *do* have user_uuid + readAt + image_512
        const existing: ReadByEntry[] = (existingRaw as any[]).filter(
          (x): x is ReadByEntry =>
            x !== null &&
            typeof x === 'object' &&
            typeof x.user_uuid === 'string' &&
            typeof x.readAt === 'string' &&
            typeof x.image_512 === 'string'
        )

        // 3) See if this user is already in the filtered list
        const idx = existing.findIndex(e => e.user_uuid === currentUser?.user_uuid)

        let updatedReadBy: ReadByEntry[]
        if (idx >= 0) {
          updatedReadBy = existing.map((e, i) => (i === idx ? { ...e, readAt: now } : e))
        } else {
          updatedReadBy = [
            ...existing,
            {
              user_uuid: currentUser.user_uuid,
              readAt: now,
              image_512: currentUser.image_512 || '',
            },
          ]
          // updatedReadBy = []
        }

        pendingBatch.current.push({
          tableName: 'COMMENTS_TABLE',
          primaryKey: 'PK',
          itemId: note.PK,
          itemSortKey: note.SK,
          fieldName: 'readBy',
          newValue: updatedReadBy,
        })

        return {
          ...note,
          readBy: updatedReadBy,
        }
      })
    )
  }, [])

  useEffect(() => {
    setLocalComments(prevLocal => {
      const existingMap = new Map<string, ReadByEntry[]>()
      prevLocal.forEach(c => {
        if (Array.isArray(c.readBy)) {
          existingMap.set(c.comment_uuid, c.readBy as ReadByEntry[])
        }
      })

      return (notes ?? []).map(incoming => {
        const kept = existingMap.get(incoming.comment_uuid)
        if (kept) {
          // if we already had a readBy locally, keep it
          return { ...incoming, readBy: kept }
        } else {
          return incoming
        }
      })
    })
  }, [notes])

  const handleOpenViewer = (attachments: Attachments[], index: number) => {
    setCurrentAttachments(attachments)
    setCurrentIndex(index)
    setViewerOpen(true)
    setIsCommentOpen(true)
  }
  const handleStartEditing = (noteId: string) => {
    setEditingNoteId(noteId)
    setIsEditing(true)
    setDeletingNoteId(null)

    // Scroll the comments container to center the target comment
    requestAnimationFrame(() => {
      const container = scrollRef.current
      const target = commentRefs.current[noteId]
      if (!container || !target) return

      const cRect = container.getBoundingClientRect()
      const tRect = target.getBoundingClientRect()
      const nextTop = container.scrollTop + (tRect.top - cRect.top) - container.clientHeight / 2 + tRect.height / 2

      container.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' })
    })
  }
  // const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, comment: Note) => {
  //   event.stopPropagation()
  //   setDeletingNoteId(comment.comment_uuid)
  //   setAnchorEl(event.currentTarget)

  //   // Scroll to the stored ref
  //   setTimeout(() => {
  //     const el = commentRefs.current[comment.comment_uuid]
  //     if (el) {
  //       el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  //     }
  //   }, 0)
  // }
  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, comment: CommentData) => {
    event.stopPropagation()
    setDeletingNoteId(comment.comment_uuid)
    setAnchorEl(event.currentTarget)

    requestAnimationFrame(() => {
      // next frame — confirm box should be rendered now
      const container = scrollRef.current
      const target = confirmRefs.current[comment.comment_uuid]
      if (!container || !target) return

      const cRect = container.getBoundingClientRect()
      const tRect = target.getBoundingClientRect()
      const top = container.scrollTop + (tRect.top - cRect.top) - container.clientHeight / 2 + tRect.height / 2

      container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
    })
  }
  const handleClosePopover = () => {
    setAnchorEl(null)
    setDeletingNoteId(null)
  }
  // Scroll when the confirm box exists in the DOM

  // const handleSetIntegrationFilter = () => {
  //   const miaNotes = localComments?.filter(n => n?.context === 'integration')
  //   setLocalComments(miaNotes)
  // }
  // const handleSetNcoFilter = () => {
  //   const ncoNotes = localComments?.filter(n => n?.context === 'nco')
  //   setLocalComments(ncoNotes)
  // }
  // const handleSetSeoFilter = () => {
  //   const seoNotes = localComments?.filter(n => n?.context === 'seo')
  //   setLocalComments(seoNotes)
  // }
  // const handleSetCreativeNotes = () => {
  //   const creativeNotes = localComments?.filter(n => n?.context === 'creative')
  //   setLocalComments(creativeNotes)
  // }
  // const handleClearFilters = () => {
  //   setLocalComments(localComments)
  // }
  const theme = useTheme()

  const accountingHeight = !expanded ? 'calc(100vh - 270px)' : 'calc(100vh - 465px)'
  const gridHeight = !expanded ? 'calc(100vh - 290px)' : 'calc(100vh - 415px)'
  return (
    <React.Fragment>
      <List
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          // bgcolor: theme.palette.background.default,
          bgcolor: 'transparent',
          '--color': theme.palette.text.link,
          borderRadius: '10px',
          overflowY: 'auto',
          maxHeight: context === 'accounting' ? accountingHeight : gridHeight,
          p: 0.5,
          px: 3,
          transition: 'all 0.3s ease',
          overscrollBehavior: 'contain',
        }}
        ref={scrollRef}
      >
        {localComments.map(note => {
          const noteOwner = users.find(user => user.user_uuid === note.owner?.[0])
          const cardRef = React.createRef<HTMLDivElement>()
          const isBeingDeleted = deletingNoteId === note.comment_uuid
          return (
            <div key={note?.SK || note.comment_uuid || note.note_id}>
              <CommentCard
                key={note.comment_uuid || note.note_id}
                note={note}
                currentUser={currentUser}
                noteOwner={noteOwner}
                editingContent={editingContent}
                setEditingContent={setEditingContent}
                onEditStart={handleStartEditing}
                onOpenViewer={handleOpenViewer}
                onDeleteAttachment={onDeleteAttachment}
                onDeleteNote={onDeleteNote}
                onDeleteClick={handleDeleteClick}
                isEditing={isEditing}
                isDeleting={isDeleting}
                deletingNoteId={deletingNoteId}
                editingNoteId={editingNoteId}
                setEditingNoteId={setEditingNoteId}
                editingFiles={editingFiles}
                setEditingFiles={setEditingFiles}
                setIsEditing={setIsEditing}
                anchorEl={anchorEl}
                scrollRef={scrollRef} // pass the scroll container
                commentRefs={commentRefs}
                confirmRefs={confirmRefs}
                onClosePopover={handleClosePopover}
                onMarkAsRead={handleMarkAsRead}
              />
            </div>
          )
        })}
      </List>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {viewerOpen && (
          <AttachmentViewer
            open={viewerOpen}
            attachments={currentAttachments}
            currentIndex={currentIndex}
            onClose={() => {
              setViewerOpen(false)
              setIsCommentOpen(false)
            }}
            onNavigate={dir => {
              if (dir === 'prev') setCurrentIndex(i => Math.max(0, i - 1))
              else setCurrentIndex(i => Math.min(currentAttachments.length - 1, i + 1))
            }}
          />
        )}
      </Box>
    </React.Fragment>
  )
}

export default CommentList
